import { createSharedComposable, useDebounceFn, useWebSocket } from '@vueuse/core';
import { computed } from 'vue';
import { $MTS, message, mitt } from '@mts-fe/utils';
import NOTICE_SOUND from '@mts/assets/NOTICE_VIDEO.mp3';
import { PageEnum } from '@mts/enums/pageEnum';
import { globSetting, PCMode } from '@mts/utils/env';
import { logError, logInfo } from '@mts/utils/log';
import { router } from '@/router';
import { getGlobalStore, getUserStore } from '@/store';

/** 消息类型 */
const MSG_TYPE = {
  /** 消息通知更新 */
  MESSAGE_UPDATE: 0,
  /** 数据更新 */
  UPDATE: 1,
  /** 消息通知 */
  NOTICE_EXCEPTION: 2,
  /** 弹窗消息通知 */
  NOTICE_MODAL: 3,
  /** 登录登出消息 */
  NOTICE_LOGIN_OUT: 4,
  /** 提示音 */
  NOTICE_SOUND: 5,
} as const;

type MSG_TYPE = (typeof MSG_TYPE)[keyof typeof MSG_TYPE];
interface MsgData<T = any> {
  msgType: MSG_TYPE;
  serviceCode?: string;
  data?: T;
}
interface WsData<T = any> extends MsgData<T> {
  timestamp: number;
  clientId: string;
  id: string;
}

/** WebSocket 事件发布订阅 */
export const wsEmitter = mitt<{
  'message-update': WsData;
  'data-update': WsData;
  // 'notice-exception': WsData;
  'notice-modal': WsData;
}>();
// 事件监听示例
// wsEmitter.on('data-update', ({ data, ...msg }) => {
//   console.log('data-update', data, msg);
// });

export const useWS = createSharedComposable(() => {
  const userStore = getUserStore();
  const globalStore = getGlobalStore();
  const stationKey = computed(() => globalStore.getStationKey);
  const clientId = computed(() => `IMS_${globSetting.device}_${stationKey.value}`);

  const serviceUrl = `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/ws`;
  const { status, open, close, send } = useWebSocket<string>(serviceUrl, {
    immediate: false,
    autoReconnect: {
      delay: 10000,
      retries: (retried: number) => {
        console.log('[WS]autoReconnect', retried);
        return retried < 99999999;
      },
      onFailed: () => logError('[WS]autoReconnect Failed！'),
    },
    heartbeat: { interval: 10000, pongTimeout: 2000 },
    onConnected: async () => {
      isConnecting = false;
      logInfo('[WS]connected：' + clientId.value);
      // 站位绑定
      sendMsg({ msgType: 1 });
    },
    onMessage: (_, event) => {
      if (event.data === 'pong') return;

      logInfo('[WS]message：' + event.data);
      onReciveMsg(event.data);
    },
    onError: (_, event) => {
      isConnecting = false;
      console.error('[WS]error：', event);
      logInfo('[WS]error：' + clientId.value);
    },
    onDisconnected(_ws, event) {
      if (document.hidden) return;

      console.warn('[WS]断开：', event);
      logInfo('[WS]断开：' + clientId.value);
      setTimeout(() => {
        if (status.value === 'CLOSED') connect();
      }, 2000);
    },
  });

  let isConnecting = false;

  const debouncedOpen = useDebounceFn(() => {
    isConnecting = true;
    logInfo('[WS]connect：' + stationKey.value);
    open();
  }, 300);

  const connect = () => {
    if (PCMode || document.hidden || !stationKey.value) return;
    if (status.value === 'OPEN' || status.value === 'CONNECTING') return;
    if (isConnecting) return;

    debouncedOpen();
  };
  $MTS.App.onResume(connect);
  document.addEventListener('visibilitychange', connect);

  function sendMsg(msgData: MsgData) {
    const msg = {
      ...msgData,
      timestamp: Date.now(),
      clientId: clientId.value,
      id: clientId.value + '__' + encodeURIComponent(location.hash),
    };
    send(JSON.stringify(msg));
  }

  const audio = new Audio(NOTICE_SOUND);
  function onReciveMsg(msg: string) {
    try {
      const res = JSON.parse(msg) as WsData;
      if (res.msgType === MSG_TYPE.UPDATE) {
        logInfo('[WS]更新下发：' + res.serviceCode, { wsMsg: res });
        wsEmitter.emit('data-update', res);
      } else if (res.msgType === MSG_TYPE.MESSAGE_UPDATE) {
        wsEmitter.emit('message-update', res);
      } else if (res.msgType === MSG_TYPE.NOTICE_EXCEPTION) {
        if (res.serviceCode === 'sus_hanging_piece_exit_rebind') {
          const data = typeof res.data === 'string' ? JSON.parse(res.data) : res.data;
          wsEmitter.emit('data-update', { ...res, data });
        } else {
          message.error(res.data?.toString());
        }
      } else if (res.msgType === MSG_TYPE.NOTICE_MODAL) {
        wsEmitter.emit('notice-modal', res);
      } else if (res.msgType === MSG_TYPE.NOTICE_LOGIN_OUT) {
        if (res.serviceCode === 'user_login_msg') {
          message.success('WS：登录成功');
          userStore.setToken(res.data?.token);
          router.replace(res.data?.homePath || PageEnum.BASE_HOME);
          userStore.getUserInfoAction();
        } else if (res.serviceCode === 'user_logout_msg') {
          message.success('WS：退出登录');
          userStore.logout(true);
        }
      } else if (res.msgType === MSG_TYPE.NOTICE_SOUND) {
        audio.play().catch(() => {});
      }
    } catch (error) {
      console.error(error);
    }
  }

  return { status, connect, open, close, sendMsg };
});
